/**
 * AÇÕES â€” Do plano à execução
 * * Módulo Principal de Gestão de Ações Estratégicas
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  ClipboardList, ArrowLeft, CheckCircle2, Clock, AlertCircle, 
  User, Calendar, Filter, Search, MessageSquare, Upload, Image,
  FileText, TrendingUp, TrendingDown, Minus, X, Plus, Activity,
  Bell, Home, Users, Map, PieChart, BarChart3, AlertTriangle, Target,
  Grid3X3, List, ArrowRight, Zap, Shield, DollarSign, FolderOpen, Save, Workflow,
  Eye, Copy, GitFork, Ban, Trash2, RefreshCw
} from 'lucide-react';
import { useAuthz } from '../hooks/useAuthz';
import { useProjectStore } from '../store/useProjectStore';
import { Action, ActionStatus, ActionPlan, CardData, ActionHealthStatus, KanbanTaskStatus } from '../types';
import { generateId } from '../utils/vsmGeometry';
import { useAuth } from '../context/AuthContext';
import { useProjectContext } from '../context/ProjectContext';
import ActionDetailView from './ActionDetailView';
import { ActionRibbon } from './ActionRibbon';
import ActionDashboard from './ActionDashboard';
import { EmptyState } from './EmptyState';
import { ActionListView } from './ActionListView';
import { ActionsKanbanBoard } from './ActionsKanbanBoard';
import { MatrizView } from './MatrizView';

interface MemoModuleProps {
  projects: Array<{
    id: string;
    nome_projeto: string;
    data_criacao: string;
    ultimo_salvamento: string;
  }>;
  currentProjectId: string | null;
  onBackToWorkflow: () => void;
  onSelectProject: (projectId: string) => void;
  zoom: number;
  onClose?: () => void;
  onNavigateToCard?: (cardId: string) => void;
  initialActionId?: string | null;
  initialProjectId?: string | null;
  onCreateProject?: () => void;
  actionRibbonSearchText?: string;
  onActionRibbonSearchChange?: (text: string) => void;
  actionRibbonViewMode?: 'list' | 'kanban' | 'matrix';
  onActionRibbonViewModeChange?: (mode: 'list' | 'kanban' | 'matrix') => void;
  externalFilteredActions?: Action[];
  onExternalUpdateAction?: (id: string, data: Partial<Action>) => void;
  focusedColumn?: 'responsavel' | 'prazo';
  onViewChange?: (view: 'dashboard' | 'project' | 'action-detail' | 'executive') => void;
  initialView?: 'canvas' | 'list' | 'kanban' | 'matrix';
  activeTab?: 'all' | 'mine' | 'doing' | 'overdue' | 'done';
  groupBy?: 'none' | 'status' | 'responsavel' | 'impacto';
  onIndicators?: () => void;
  onImportExcel?: () => void;
  onDownloadTemplate?: () => void;
  onSaveLocal?: () => void;
  onOpenLocal?: () => void;
  onExportPDF?: (type?: 'list' | 'kanban' | 'matrix' | 'indicators') => void;
  onExportImage?: (type?: 'list' | 'kanban' | 'matrix' | 'indicators') => void;
  onNewMap?: () => void;
  onSummaryChange?: (summary: { overdue: number; doing: number; done: number; incomplete: number }) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  initialIndicatorsOpen?: boolean;
  showRibbon?: boolean;
  selectedSector?: string;
  onSelectedSectorChange?: (value: string) => void;
  selectedResponsible?: string;
  onSelectedResponsibleChange?: (value: string) => void;
  selectedStatus?: string;
  onSelectedStatusChange?: (value: string) => void;
  actionScope?: 'current' | 'all' | 'specific';
  onActionScopeChange?: (value: 'current' | 'all' | 'specific') => void;
  specificProjectId?: string | null;
  onSpecificProjectIdChange?: (value: string | null) => void;
}

type MemoView = 'dashboard' | 'project' | 'action-detail' | 'executive';

const MemoModule: React.FC<MemoModuleProps> = ({
  projects,
  currentProjectId,
  onBackToWorkflow,
  onSelectProject,
  onClose,
  initialActionId,
  initialProjectId,
  onNavigateToCard,
  onCreateProject,
  actionRibbonSearchText,
  onActionRibbonSearchChange,
  actionRibbonViewMode,
  onActionRibbonViewModeChange,
  externalFilteredActions,
  onExternalUpdateAction,
  focusedColumn,
  onViewChange,
  zoom,
  initialView = 'list',
  activeTab = 'all',
  groupBy = 'none',
  onIndicators,
  onImportExcel,
  onDownloadTemplate,
  onSaveLocal,
  onOpenLocal,
  onExportPDF,
  onExportImage,
  onNewMap,
  onSummaryChange,
  onUndo,
  onRedo,
  initialIndicatorsOpen = false,
  showRibbon = true,
  selectedSector: controlledSelectedSector,
  onSelectedSectorChange,
  selectedResponsible: controlledSelectedResponsible,
  onSelectedResponsibleChange,
  selectedStatus: controlledSelectedStatus,
  onSelectedStatusChange,
  actionScope: controlledActionScope,
  onActionScopeChange,
  specificProjectId: controlledSpecificProjectId,
  onSpecificProjectIdChange,
}) => {
  const { actions: storeActions, actionPlans, organization, getActionsByProject, getCards, addAction, updateAction: storeUpdateAction } = useProjectStore();
  
  const actions = externalFilteredActions || storeActions;
  const updateAction = onExternalUpdateAction ?? storeUpdateAction;
  const { user } = useAuth();
  const { can } = useAuthz();
  const { selectedProject, isCreateActionModalOpen, setCreateActionModalOpen, isProjectLoading } = useProjectContext();
  const location = useLocation();
  
  // 1. Controle de Visualização Principal
  const [view, setView] = useState<MemoView>('dashboard');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  
  // 2. Controle do Modo de Visualização
  const [viewMode, setViewMode] = useState<'canvas' | 'list' | 'kanban' | 'matrix'>(() => {
    if (initialView === 'canvas') return 'list'; 
    return initialView;
  });

  useEffect(() => {
    if (initialView && initialView !== 'canvas') {
      setViewMode(initialView);
      if (onActionRibbonViewModeChange) {
        onActionRibbonViewModeChange(initialView as 'list' | 'kanban' | 'matrix');
      }
    }
  }, [initialView, onActionRibbonViewModeChange]);

  useEffect(() => {
    setIndicatorsOpen(initialIndicatorsOpen);
  }, [initialIndicatorsOpen]);

  const [localSearchText, setLocalSearchText] = useState('');
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const [indicatorsOpen, setIndicatorsOpen] = useState(initialIndicatorsOpen);
  const [selectedActionIds, setSelectedActionIds] = useState<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; actionId: string } | null>(null);
  const [lastClickedId, setLastClickedId] = useState<string | null>(null);
  
  // Estados de zoom independentes para cada modo
  const [zoomList, setZoomList] = useState(1);
  const [zoomKanban, setZoomKanban] = useState(1);
  const [zoomMatrix, setZoomMatrix] = useState(1);
  const [zoomDetail, setZoomDetail] = useState(1);
  
  // Sincronizar zoom externo com o modo atual
  useEffect(() => {
    switch(viewMode) {
      case 'list':
        setZoomList(zoom);
        break;
      case 'kanban':
        setZoomKanban(zoom);
        break;
      case 'matrix':
        setZoomMatrix(zoom);
        break;
    }
  }, [zoom, viewMode]);
  
  // Estados para filtros da Context Bar
  const [internalSelectedSector, setInternalSelectedSector] = useState('all');
  const [internalSelectedResponsible, setInternalSelectedResponsible] = useState('all');
  const [internalSelectedStatus, setInternalSelectedStatus] = useState('all');
  
  // Estados para controle de escopo de ações
  const [internalActionScope, setInternalActionScope] = useState<'current' | 'all' | 'specific'>('current');
  const [internalSpecificProjectId, setInternalSpecificProjectId] = useState<string | null>(null);

  const selectedSector = controlledSelectedSector ?? internalSelectedSector;
  const selectedResponsible = controlledSelectedResponsible ?? internalSelectedResponsible;
  const selectedStatus = controlledSelectedStatus ?? internalSelectedStatus;
  const actionScope = controlledActionScope ?? internalActionScope;
  const specificProjectId = controlledSpecificProjectId ?? internalSpecificProjectId;

  const setSelectedSector = useCallback((value: string) => {
    if (onSelectedSectorChange) {
      onSelectedSectorChange(value);
      return;
    }
    setInternalSelectedSector(value);
  }, [onSelectedSectorChange]);

  const setSelectedResponsible = useCallback((value: string) => {
    if (onSelectedResponsibleChange) {
      onSelectedResponsibleChange(value);
      return;
    }
    setInternalSelectedResponsible(value);
  }, [onSelectedResponsibleChange]);

  const setSelectedStatus = useCallback((value: string) => {
    if (onSelectedStatusChange) {
      onSelectedStatusChange(value);
      return;
    }
    setInternalSelectedStatus(value);
  }, [onSelectedStatusChange]);

  const setActionScope = useCallback((value: 'current' | 'all' | 'specific') => {
    if (onActionScopeChange) {
      onActionScopeChange(value);
      return;
    }
    setInternalActionScope(value);
  }, [onActionScopeChange]);

  const setSpecificProjectId = useCallback((value: string | null) => {
    if (onSpecificProjectIdChange) {
      onSpecificProjectIdChange(value);
      return;
    }
    setInternalSpecificProjectId(value);
  }, [onSpecificProjectIdChange]);
  
  // Reset scope quando montar ou projeto mudar
  useEffect(() => {
    setActionScope('current');
    setSpecificProjectId(null);
  }, [currentProjectId]);
  
  // Handler para limpar filtros
  const handleClearFilters = () => {
    setSelectedSector('all');
    setSelectedResponsible('all');
    setSelectedStatus('all');
    setLocalSearchText('');
  };
  
  // Handler para exportar ações para Excel
  const handleExportActionsToExcel = () => {
    // Usar as ações filtradas que estão sendo exibidas
    const actionsToExport = filteredActions;
    
    if (actionsToExport.length === 0) {
      alert('Nenhuma ação para exportar');
      return;
    }
    
    // Criar conteúdo CSV
    const headers = ['ID', 'Título', 'Status', 'Responsável', 'Prazo', 'Departamento', 'Impacto', 'Esforço', 'Descrição', 'Card Vinculado'];
    const cards = getCards?.() || [];
    const rows = actionsToExport.map(action => {
      const cardInfo = action.linkedNodeId ? cards.find(c => c.id === action.linkedNodeId) : null;
      const cardNumber = cardInfo ? (cardInfo.numero_sequencial || 'S/N') : '-';
      
      return [
        action.id.substring(0, 8),
        action.title || '-',
        action.status === 'todo' ? 'A Fazer' : action.status === 'doing' ? 'Em Andamento' : action.status === 'done' ? 'Concluído' : 'Cancelada',
        action.responsavel || '-',
        action.prazo || '-',
        action.cardData?.setor || 'Geral',
        action.impacto ? `${action.impacto}` : '-',
        action.esforco ? `${action.esforco}` : '-',
        (action.description || '').replace(/"/g, '""'), // Escape aspas
        cardNumber
      ];
    });
    
    // Montar CSV
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Criar BOM para UTF-8 (Excel reconhece corretamente)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `acoes_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Handler para gerar relatório completo de ações
  const handleGenerateFullReport = () => {
    const actionsToReport = filteredActions;
    
    if (actionsToReport.length === 0) {
      alert('Nenhuma ação para gerar relatório');
      return;
    }
    
    const projectName = projects.find(p => p.id === currentProjectId)?.nome_projeto || 'Projeto';
    const reportDate = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    
    // Estatísticas gerais
    const totalActions = actionsToReport.length;
    const todoCount = actionsToReport.filter(a => a.status === 'todo').length;
    const doingCount = actionsToReport.filter(a => a.status === 'doing').length;
    const doneCount = actionsToReport.filter(a => a.status === 'done').length;
    const canceledCount = actionsToReport.filter(a => a.status === 'canceled').length;
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Relatório de Ações - ${projectName}</title>
  <style>
    @page { size: A4; margin: 2cm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1e293b; line-height: 1.6; background: white; }
    .container { max-width: 210mm; margin: 0 auto; padding: 20px; }
    .header { text-align: center; margin-bottom: 40px; border-bottom: 3px solid #3b82f6; padding-bottom: 20px; }
    .header h1 { font-size: 28px; color: #1e40af; margin-bottom: 8px; }
    .header .subtitle { font-size: 14px; color: #64748b; }
    .header .date { font-size: 12px; color: #94a3b8; margin-top: 5px; }
    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 40px; }
    .summary-card { background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%); padding: 15px; border-radius: 8px; text-align: center; border-left: 4px solid #3b82f6; }
    .summary-card .label { font-size: 11px; color: #64748b; text-transform: uppercase; font-weight: 600; margin-bottom: 5px; }
    .summary-card .value { font-size: 28px; font-weight: bold; color: #1e40af; }
    .action { page-break-inside: avoid; margin-bottom: 30px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
    .action-header { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; padding: 15px 20px; }
    .action-header h2 { font-size: 16px; margin-bottom: 5px; }
    .action-header .meta { font-size: 11px; opacity: 0.9; display: flex; gap: 15px; flex-wrap: wrap; }
    .action-body { padding: 20px; background: #fafafa; }
    .field { margin-bottom: 15px; }
    .field-label { font-size: 10px; color: #64748b; text-transform: uppercase; font-weight: 600; margin-bottom: 3px; }
    .field-value { font-size: 13px; color: #1e293b; background: white; padding: 8px 12px; border-radius: 4px; border: 1px solid #e2e8f0; }
    .field-value.empty { color: #94a3b8; font-style: italic; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 600; }
    .status-todo { background: #f1f5f9; color: #475569; }
    .status-doing { background: #dbeafe; color: #1e40af; }
    .status-done { background: #d1fae5; color: #065f46; }
    .status-canceled { background: #f1f5f9; color: #64748b; }
    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
    .checklist { list-style: none; }
    .checklist-item { padding: 8px; background: white; margin-bottom: 5px; border-radius: 4px; border-left: 3px solid #e2e8f0; font-size: 12px; }
    .checklist-item.checked { border-left-color: #10b981; color: #059669; }
    .checklist-item.checked::before { content: 'âœ“ '; font-weight: bold; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 11px; color: #94a3b8; }
    @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>ðŸ“Š Relatório de Ações</h1>
      <div class="subtitle">${projectName}</div>
      <div class="date">Gerado em ${reportDate}</div>
    </div>
    
    <div class="summary">
      <div class="summary-card">
        <div class="label">Total</div>
        <div class="value">${totalActions}</div>
      </div>
      <div class="summary-card">
        <div class="label">A Fazer</div>
        <div class="value">${todoCount}</div>
      </div>
      <div class="summary-card">
        <div class="label">Em Andamento</div>
        <div class="value">${doingCount}</div>
      </div>
      <div class="summary-card">
        <div class="label">Concluídas</div>
        <div class="value">${doneCount}</div>
      </div>
    </div>
    
    ${actionsToReport.map((action, index) => {
      const statusLabel = action.status === 'todo' ? 'A Fazer' : action.status === 'doing' ? 'Em Andamento' : action.status === 'done' ? 'Concluído' : 'Cancelada';
      const statusClass = `status-${action.status}`;
      const cardInfo = action.linkedNodeId ? (getCards?.() || []).find(c => c.id === action.linkedNodeId) : null;
      const checklist = (action as any).checklist || action.actionPlan?.checklist || [];
      
      return `
        <div class="action">
          <div class="action-header">
            <h2>#${index + 1} - ${action.title || 'Sem título'}</h2>
            <div class="meta">
              <span>ðŸ“‹ Status: <strong>${statusLabel}</strong></span>
              ${action.responsavel ? `<span>ðŸ‘¤ ${action.responsavel}</span>` : ''}
              ${action.prazo ? `<span>ðŸ“… ${new Date(action.prazo).toLocaleDateString('pt-BR')}</span>` : ''}
              ${action.impacto ? `<span>âš¡ Impacto: ${action.impacto}/3</span>` : ''}
              ${action.esforco ? `<span>ðŸ’ª Esforço: ${action.esforco}/3</span>` : ''}
            </div>
          </div>
          <div class="action-body">
            ${action.description ? `
              <div class="field">
                <div class="field-label">Descrição</div>
                <div class="field-value">${action.description}</div>
              </div>
            ` : ''}
            
            <div class="grid-2">
              ${action.actionPlan?.oQue ? `
                <div class="field">
                  <div class="field-label">ðŸŽ¯ O Que</div>
                  <div class="field-value">${action.actionPlan.oQue}</div>
                </div>
              ` : ''}
              
              ${action.actionPlan?.porque ? `
                <div class="field">
                  <div class="field-label">â“ Por Que</div>
                  <div class="field-value">${action.actionPlan.porque}</div>
                </div>
              ` : ''}
              
              ${action.actionPlan?.onde ? `
                <div class="field">
                  <div class="field-label">ðŸ“ Onde</div>
                  <div class="field-value">${action.actionPlan.onde}</div>
                </div>
              ` : ''}
              
              ${action.actionPlan?.quem ? `
                <div class="field">
                  <div class="field-label">ðŸ‘¤ Quem</div>
                  <div class="field-value">${action.actionPlan.quem}</div>
                </div>
              ` : ''}
              
              ${action.actionPlan?.quando ? `
                <div class="field">
                  <div class="field-label">ðŸ“… Quando</div>
                  <div class="field-value">${action.actionPlan.quando}</div>
                </div>
              ` : ''}
              
              ${action.actionPlan?.como ? `
                <div class="field">
                  <div class="field-label">ðŸ”§ Como</div>
                  <div class="field-value">${action.actionPlan.como}</div>
                </div>
              ` : ''}
              
              ${action.actionPlan?.quanto ? `
                <div class="field">
                  <div class="field-label">ðŸ’° Quanto</div>
                  <div class="field-value">R$ ${action.actionPlan.quanto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                </div>
              ` : ''}
              
              ${action.cardData?.setor ? `
                <div class="field">
                  <div class="field-label">ðŸ¢ Setor</div>
                  <div class="field-value">${action.cardData.setor}</div>
                </div>
              ` : ''}
              
              ${cardInfo ? `
                <div class="field">
                  <div class="field-label">ðŸ—ºï¸ Card Vinculado</div>
                  <div class="field-value">#${cardInfo.numero_sequencial} - ${cardInfo.atividade || 'Card'}</div>
                </div>
              ` : ''}
            </div>
            
            ${checklist.length > 0 ? `
              <div class="field">
                <div class="field-label">âœ… Checklist (${checklist.filter((t: any) => t.checked).length}/${checklist.length})</div>
                <ul class="checklist">
                  ${checklist.map((item: any) => `<li class="checklist-item ${item.checked ? 'checked' : ''}">${item.text}</li>`).join('')}
                </ul>
              </div>
            ` : ''}
          </div>
        </div>
      `;
    }).join('')}
    
    <div class="footer">
      <p>Relatório gerado pelo Sistema WorkFlow</p>
      <p>Â© ${new Date().getFullYear()} - Todos os direitos reservados</p>
    </div>
  </div>
</body>
</html>
    `;
    
    // Abrir relatório em nova janela
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
    }
  };
  
  // Handler para tecla Delete
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' && selectedActionIds.size > 0 && viewMode === 'list') {
        // ðŸš« Verificar se alguma ação selecionada tem data postergada
        const selectedActions = actions.filter(a => selectedActionIds.has(a.id));
        const actionsWithPostponedDates = selectedActions.filter(a => a.deadlineHistory && a.deadlineHistory.length > 0);
        
        if (actionsWithPostponedDates.length > 0) {
          alert(`âš ï¸ ${actionsWithPostponedDates.length} ação(ões) selecionada(s) possui(em) datas postergadas e não pode(m) ser excluída(s). Ações com histórico de prorrogação devem permanecer visíveis para rastreabilidade.`);
          return;
        }
        
        const count = selectedActionIds.size;
        if (confirm(`Deseja realmente excluir ${count} ${count === 1 ? 'ação' : 'ações'}?`)) {
          const deleteAction = useProjectStore.getState().deleteAction;
          selectedActionIds.forEach(id => deleteAction(id));
          setSelectedActionIds(new Set());
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedActionIds, viewMode, actions]);
  
  // Reportar mudanças de view
  useEffect(() => {
    if (onViewChange) onViewChange(view);
  }, [view, onViewChange]);

  const searchText = actionRibbonSearchText ?? localSearchText;

  // Reagir a mudanças de projeto selecionado
  useEffect(() => {
    if (selectedProject) {
      setSelectedProjectId(selectedProject.id);
    }
  }, [selectedProject]);

  // Filtragem Geral
  const filteredActions = useMemo(() => {
    let filtered = [...actions];

    // Filtro de Escopo (Projeto)
    if (actionScope === 'current' && currentProjectId) {
      // Mostrar apenas ações do projeto atual
      filtered = filtered.filter(action => action.projectId === currentProjectId);
    } else if (actionScope === 'specific' && specificProjectId) {
      // Mostrar ações de um projeto específico
      filtered = filtered.filter(action => action.projectId === specificProjectId);
    }
    // Se actionScope === 'all', não filtra por projeto

    // Filtro de busca
    if (searchText?.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(action =>
        action.title.toLowerCase().includes(searchLower) ||
        action.responsavel?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filtro de Status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(action => action.status === selectedStatus);
    }
    
    // Filtro de Setor
    if (selectedSector !== 'all') {
      filtered = filtered.filter(action => action.cardData?.setor === selectedSector);
    }
    
    // Filtro de Responsável
    if (selectedResponsible !== 'all') {
      filtered = filtered.filter(action => action.responsavel === selectedResponsible);
    }
    
    return filtered;
  }, [actions, searchText, selectedStatus, selectedSector, selectedResponsible, actionScope, currentProjectId, specificProjectId]);

  const sectors = useMemo(() => Array.from(new Set(actions.map(a => a.cardData?.setor || 'Geral').filter(Boolean))).sort(), [actions]);
  
  const responsibles = useMemo(() => {
    const unique = new Set<string>();
    actions.forEach(a => { 
      if (a.responsavel && a.responsavel.trim()) {
        unique.add(a.responsavel); 
      }
    });
    return Array.from(unique).sort();
  }, [actions]);

  const summary = useMemo(() => {
    const done = filteredActions.filter(a => a.status === 'done').length;
    const doing = filteredActions.filter(a => a.status === 'doing').length;
    const overdue = filteredActions.filter(a => a.status !== 'done' && a.deadline && new Date(a.deadline) < new Date()).length;
    // Ações incompletas: sem título, sem responsável ou sem prazo
    const incomplete = filteredActions.filter(a => 
      a.status !== 'done' && (!a.title?.trim() || !a.responsavel?.trim() || !a.deadline)
    ).length;
    return { done, doing, overdue, incomplete };
  }, [filteredActions]);

  // Notificar mudanças no summary
  useEffect(() => {
    if (onSummaryChange) {
      onSummaryChange(summary);
    }
  }, [summary, onSummaryChange]);

  const handleUpdateMatrixPriority = useCallback((actionId: string, data: { effort?: number; impact?: number }) => {
    if (data.effort == null && data.impact == null) {
      // Removido da matriz (voltou ao backlog) â€” limpar classificação
      updateAction(actionId, {
        effort: undefined, impact: undefined,
        impacto: undefined, esforco: undefined,
        priority: undefined,
      });
      return;
    }
    const legacyEsforco = (data.effort ?? 0) < 33 ? 1 : (data.effort ?? 0) < 66 ? 2 : 3;
    const legacyImpacto = (data.impact ?? 0) < 33 ? 1 : (data.impact ?? 0) < 66 ? 2 : 3;
    updateAction(actionId, {
      effort: data.effort, impact: data.impact,
      impacto: legacyImpacto as 1|2|3,
      esforco: legacyEsforco as 1|2|3,
      priority: { impacto: legacyImpacto as 1|2|3, esforco: legacyEsforco as 1|2|3 }
    });
  }, [updateAction]);

  // --- FUNÇÃO PARA ADICIONAR AÇÃO RÁPIDA NO KANBAN ---
  const handleQuickAddAction = (status: KanbanTaskStatus) => {
    if (!selectedProject?.id) return;
    
    const newAction: Action = {
      id: generateId(),
      title: 'Nova Ação',
      description: '',
      status: status,
      priority: 'medium',
      responsible: '',
      deadline: null,
      projectId: selectedProject.id,
      impacto: 1,
      esforco: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    addAction(newAction);
    // Opcional: Abrir detalhes logo após criar
    // setSelectedActionId(newAction.id);
    // setView('action-detail');
  };

  // Navegação Inicial
  useEffect(() => {
    if (initialActionId) {
      setSelectedActionId(initialActionId);
      setView('action-detail');
    }
  }, [initialActionId]);

  // --- RENDERIZADORES ---

  // Selecionar o que renderizar (Detalhes)
  if (view === 'action-detail' && selectedActionId) {
    const action = actions.find(a => a.id === selectedActionId);
    if (action) {
      return (
        <ActionDetailView
          action={action}
          onBack={() => {
            setSelectedActionId(null);
            setView('dashboard');
          }}
          onUpdateAction={updateAction}
          zoom={zoomDetail}
        />
      );
    }
  }

  // Dashboard Principal (Lista/Kanban/Matriz)
  return (
    <div className="flex flex-col h-full bg-[var(--bg-surface)] font-sans">
      {/* Ribbon de Ações (Nível 1) */}
      {showRibbon ? (
        <ActionRibbon
        searchText={searchText}
        onSearchChange={onActionRibbonSearchChange || setLocalSearchText}
        viewMode={viewMode === 'canvas' ? 'list' : viewMode as any}
        onViewModeChange={(mode) => {
          setViewMode(mode);
          setIndicatorsOpen(false); // Fechar indicadores ao mudar de view
          if (onActionRibbonViewModeChange) onActionRibbonViewModeChange(mode);
        }}
        onNewAction={() => {
          if (!currentProjectId) {
            // Sinalizar necessidade de projeto via evento global
            window.dispatchEvent(new CustomEvent('projectRequired', { 
              detail: { action: 'action' } 
            }));
            return;
          }
          setCreateActionModalOpen(true);
        }}
        goHome={onBackToWorkflow}
        onSave={() => useProjectStore.getState().setSaveStatus('saving')}
        onIndicators={() => { setIndicatorsOpen(!indicatorsOpen); }}
        onProjectSettings={() => {
          // Abrir modal de informações do projeto
          if (onBackToWorkflow) {
            // Navegar de volta para o workflow onde o modal está renderizado
            onBackToWorkflow();
            // Sinalizar abertura do modal via callback ou evento
            setTimeout(() => {
              const event = new CustomEvent('openProjectInfo');
              window.dispatchEvent(event);
            }, 100);
          }
        }}
        onImportExcel={onImportExcel}
        onExportExcel={handleExportActionsToExcel}
        onGenerateFullReport={handleGenerateFullReport}
        onExportPDF={onExportPDF}
        onExportImage={onExportImage}
        onDownloadTemplate={onDownloadTemplate}
        onSaveLocal={onSaveLocal}
        onOpenLocal={onOpenLocal}
        onNewMap={onNewMap}
        mode="action_plan"
        indicatorsOpen={indicatorsOpen}
        sectors={sectors}
        selectedSector={selectedSector}
        onSectorChange={setSelectedSector}
        responsibles={responsibles}
        selectedResponsible={selectedResponsible}
        onResponsibleChange={setSelectedResponsible}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        actionScope={actionScope}
        onActionScopeChange={setActionScope}
        specificProjectId={specificProjectId}
        onSpecificProjectChange={setSpecificProjectId}
        projects={projects}
        currentProjectName={projects.find(p => p.id === currentProjectId)?.nome_projeto || 'Projeto Atual'}
        onUndo={onUndo}
        onRedo={onRedo}
        />
      ) : null}

      {/* ====== MODAL: NOVA AÇÃO ====== */}
      {isCreateActionModalOpen && (() => {
        const fromIssue = (location.state as any)?.fromIssue as { id: string; title: string; severity?: string; description?: string } | undefined;
        const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          const title = (fd.get('title') as string ?? '').trim();
          if (!title) return;
          addAction({
            title,
            description: (fd.get('description') as string ?? '').trim() || undefined,
            status: (fd.get('status') as Action['status']) ?? 'todo',
            responsible: (fd.get('responsible') as string ?? '').trim() || undefined,
            responsavel: (fd.get('responsible') as string ?? '').trim() || undefined,
            deadline: (fd.get('deadline') as string ?? '') || undefined,
            prazo: (fd.get('deadline') as string ?? '') || undefined,
            projectId: currentProjectId ?? undefined,
            originSource: fromIssue ? 'Problema' : 'Manual',
          });
          setCreateActionModalOpen(false);
          if (fromIssue) window.history.replaceState({}, '');
        };
        return (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setCreateActionModalOpen(false)} />
            <div className="relative z-10 w-full max-w-sm rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-[var(--bg-surface)] shadow-2xl">
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-4 py-3">
                <span className="text-[13px] font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">Nova Ação</span>
                <button type="button" onClick={() => setCreateActionModalOpen(false)} className="rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                  <X size={16} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-3">
                {/* Título — obrigatório */}
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Título *</label>
                  <input
                    name="title"
                    required
                    autoFocus
                    defaultValue={fromIssue ? `[Problema] ${fromIssue.title}` : undefined}
                    placeholder="O que precisa ser feito?"
                    className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                {/* Responsável + Prazo — linha rápida */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Responsável</label>
                    <input
                      name="responsible"
                      placeholder="Nome"
                      className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Prazo</label>
                    <input
                      type="date"
                      name="deadline"
                      className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
                {/* Descrição — colapsável, secundária */}
                <details className="group">
                  <summary className="cursor-pointer list-none text-[11px] font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-500 dark:hover:text-slate-300 select-none flex items-center gap-1">
                    <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
                    Descrição / Contexto
                  </summary>
                  <textarea
                    name="description"
                    rows={2}
                    defaultValue={fromIssue ? `Originado do problema: ${fromIssue.title}${fromIssue.severity ? ` (Severidade: ${fromIssue.severity})` : ''}${fromIssue.description ? `\n${fromIssue.description}` : ''}` : undefined}
                    placeholder="Detalhes, contexto, por quê..."
                    className="mt-1.5 w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none resize-none"
                  />
                </details>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setCreateActionModalOpen(false)} className="rounded px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                    Cancelar
                  </button>
                  <button type="submit" className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                    Criar Ação
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Menu de Contexto */}
      {contextMenu && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setContextMenu(null)}
          />
          <div 
            className="fixed z-50 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-100 dark:border-slate-700 py-1 min-w-[200px]"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            {selectedActionIds.size > 1 ? (
              // Menu para múltiplas seleções
              <>
                <div className="px-4 py-2 text-xs text-slate-500 font-semibold border-b border-slate-100">
                  {selectedActionIds.size} ações selecionadas
                </div>
                <button
                  onClick={() => {
                    // ðŸš« Verificar se alguma ação selecionada tem data postergada
                    const selectedActions = actions.filter(a => selectedActionIds.has(a.id));
                    const actionsWithPostponedDates = selectedActions.filter(a => a.deadlineHistory && a.deadlineHistory.length > 0);
                    
                    if (actionsWithPostponedDates.length > 0) {
                      alert(`âš ï¸ ${actionsWithPostponedDates.length} ação(ões) possui(em) datas postergadas e não pode(m) ser excluída(s).`);
                      setContextMenu(null);
                      return;
                    }
                    
                    const count = selectedActionIds.size;
                    if (confirm(`Deseja realmente excluir ${count} ações?`)) {
                      const deleteAction = useProjectStore.getState().deleteAction;
                      selectedActionIds.forEach(id => deleteAction(id));
                      setSelectedActionIds(new Set());
                    }
                    setContextMenu(null);
                  }}
                  disabled={!can('delete', 'actions')}
                  className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-red-500 ${
                    can('delete', 'actions')
                      ? 'hover:bg-red-50'
                      : 'opacity-50 pointer-events-none'
                  }`}
                >
                  <Trash2 size={16} className="mr-1" /> Excluir Selecionadas
                </button>
              </>
            ) : (
              // Menu para ação individual
              <>
                <button
                  onClick={() => {
                    const action = actions.find(a => a.id === contextMenu.actionId);
                    if (action) {
                      setSelectedActionId(contextMenu.actionId);
                      setView('action-detail');
                    }
                    setContextMenu(null);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2"
                >
                  <Eye size={16} className="mr-1" /> Abrir Detalhes
                </button>
                <button
                  onClick={() => {
                    const action = actions.find(a => a.id === contextMenu.actionId);
                    if (action) {
                      const newAction = { ...action, id: generateId(), title: `${action.title} (Cópia)` };
                      addAction(newAction);
                    }
                    setContextMenu(null);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2"
                >
                  <Copy size={16} className="mr-1" /> Duplicar
                </button>
                <button
                  onClick={() => {
                    const action = actions.find(a => a.id === contextMenu.actionId);
                    if (action) {
                      // Criar ação filha condicionada à conclusão da ação pai
                      const newAction: Action = {
                        id: generateId(),
                        title: `Após "${action.title}"`,
                        status: 'todo',
                        parentActionId: action.id,
                        dependsOnActionId: action.id,
                        projectId: action.projectId || currentProjectId,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        responsavel: action.responsavel,
                        impacto: action.impacto,
                        esforco: action.esforco
                      };
                      addAction(newAction);
                      setContextMenu(null);
                      setSelectedActionId(newAction.id);
                      setView('action-detail');
                    }
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2"
                >
                  <GitFork size={16} className="mr-1" /> Criar Ação Filha Condicionada
                </button>
                <div className="border-t border-slate-100 my-1" />
                <button
                  onClick={() => {
                    const action = actions.find(a => a.id === contextMenu.actionId);
                    if (action) {
                      // ðŸš« Não permite cancelar ações com datas postergadas
                      if (action.deadlineHistory && action.deadlineHistory.length > 0) {
                        alert('âš ï¸ Não é possível cancelar ações com datas postergadas. A ação permanece visível para rastreabilidade.');
                        setContextMenu(null);
                        return;
                      }
                      const newStatus = action.status === 'canceled' ? 'todo' : 'canceled';
                      updateAction(action.id, { status: newStatus });
                    }
                    setContextMenu(null);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-amber-50 text-amber-500 flex items-center gap-2"
                >
                  {(() => {
                    const action = actions.find(a => a.id === contextMenu.actionId);
                    return action?.status === 'canceled' 
                      ? <><RefreshCw size={16} className="mr-1" /> Reativar Ação</> 
                      : <><Ban size={16} className="mr-1" /> Cancelar Ação</>;
                  })()}
                </button>
                <button
                  onClick={() => {
                    const action = actions.find(a => a.id === contextMenu.actionId);
                    // ðŸš« Não permite excluir ações com datas postergadas
                    if (action?.deadlineHistory && action.deadlineHistory.length > 0) {
                      alert('âš ï¸ Não é possível excluir ações com datas postergadas. Use "Cancelar" se necessário, mas a ação permanece visível para rastreabilidade.');
                      setContextMenu(null);
                      return;
                    }
                    if (confirm('Deseja realmente excluir esta ação?')) {
                      useProjectStore.getState().deleteAction(contextMenu.actionId);
                    }
                    setContextMenu(null);
                  }}
                  disabled={!can('delete', 'actions')}
                  className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-red-500 ${
                    can('delete', 'actions')
                      ? 'hover:bg-red-50'
                      : 'opacity-50 pointer-events-none'
                  }`}
                >
                  <Trash2 size={16} className="mr-1" /> Excluir
                </button>
              </>
            )}
          </div>
        </>
      )}

      <div className="flex-1 overflow-hidden relative">
        {isProjectLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* PAINEL DE INDICADORES */}
            {indicatorsOpen ? (
              <div className="h-full overflow-hidden bg-white dark:bg-[var(--bg-surface)]">
                <ActionDashboard 
                  actions={filteredActions}
                  projects={projects}
                  organization={organization}
                />
              </div>
            ) : (
              <>
                {/* MODO LISTA */}
                {viewMode === 'list' && (
                  <div className="h-full overflow-hidden flex flex-col">
                    {filteredActions.length === 0 ? (
                      <div className="flex-1 flex items-center justify-center">
                        <EmptyState
                          onNewAction={() => setCreateActionModalOpen(true)}
                        />
                      </div>
                    ) : (
                      <div className="flex-1 overflow-auto bg-[var(--bg-surface)]">
                        <ActionListView
                          actions={filteredActions}
                          cards={getCards?.() || []}
                          onActionClick={(id, event) => {
                            // Seleção múltipla com Ctrl/Cmd
                            if (event?.ctrlKey || event?.metaKey) {
                              setSelectedActionIds(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(id)) newSet.delete(id);
                                else newSet.add(id);
                                return newSet;
                              });
                              setLastClickedId(id);
                            }
                            // Seleção em range com Shift
                            else if (event?.shiftKey && lastClickedId) {
                              const currentIndex = filteredActions.findIndex(a => a.id === id);
                              const lastIndex = filteredActions.findIndex(a => a.id === lastClickedId);
                              const start = Math.min(currentIndex, lastIndex);
                              const end = Math.max(currentIndex, lastIndex);
                              const rangeIds = filteredActions.slice(start, end + 1).map(a => a.id);
                              setSelectedActionIds(new Set(rangeIds));
                            }
                            // Clique normal - abrir detalhes
                            else {
                              setSelectedActionId(id);
                              setView('action-detail');
                              setSelectedActionIds(new Set());
                              setLastClickedId(id);
                            }
                          }}
                          onEditAction={(id) => { setSelectedActionId(id); setView('action-detail'); }}
                          onUpdateAction={updateAction}
                          onContextMenu={(id, event) => {
                            event.preventDefault();
                            setContextMenu({ x: event.clientX, y: event.clientY, actionId: id });
                          }}
                          selectedActionIds={selectedActionIds}
                          onDeleteAction={(id) => useProjectStore.getState().deleteAction(id)}
                          onNavigateToMatrix={(actionId) => {
                            // Navegar para matriz e selecionar a ação
                            setViewMode('matrix');
                            if (onActionRibbonViewModeChange) {
                              onActionRibbonViewModeChange('matrix');
                            }
                            // Limpar seleções
                            setSelectedActionIds(new Set([actionId]));
                            setLastClickedId(actionId);
                          }}
                          onNavigateToCard={(cardId) => {
                            // Chamar função de navegação para o card no mapa VSM
                            onNavigateToCard?.(cardId);
                          }}
                          activeTab={activeTab}
                          groupBy={groupBy}
                          zoom={zoomList}
                          focusedColumn={focusedColumn}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* MODO KANBAN */}
                {viewMode === 'kanban' && (
                  <div className="h-full overflow-hidden bg-gray-50">
                    <ActionsKanbanBoard
                      actions={filteredActions}
                      zoomLevel={zoomKanban}
                      onUpdateAction={updateAction}
                      onActionClick={(id) => { setSelectedActionId(id); setView('action-detail'); }}
                      onAddAction={handleQuickAddAction} // Passando a função
                    />
                  </div>
                )}

                {/* MODO MATRIZ */}
                {viewMode === 'matrix' && (
                  <div className="h-full overflow-hidden bg-white dark:bg-[var(--bg-surface)]">
                    <MatrizView 
                      actions={filteredActions}
                      onUpdateAction={handleUpdateMatrixPriority}
                      cards={getCards?.() || []}
                      zoom={zoomMatrix}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MemoModule;
